<template>
  <div class="candidate-fields">
    <p class="text-muted mb-2" v-if="!values.length && !isAdding">{{ $t('candidate.fields.empty') }}</p>

    <ul class="fields-list" v-if="values.length">
      <li v-for="value in values" :key="value.field_key" class="field-row">
        <span class="field-label text-truncate">{{ value.label }}</span>

        <div class="field-value">
          <CandidateFieldInput
            v-if="editingKey === value.field_key"
            :definition="asDefinition(value)"
            :model-value="draft"
            :disabled="Boolean(savingKey)"
            @update:model-value="(v) => (draft = v)"
            @model-value-and-submit="(v) => saveValue(value.field_key, v)"
            @submit="saveValue(value.field_key, draft)"
            @cancel="cancelEdit" />
          <button type="button" class="field-value-display text-truncate" v-else @click="startEdit(value)">
            <span v-if="value.value !== null && value.value !== ''">{{ formatValue(value) }}</span>
            <span v-else class="text-muted">{{ $t('candidate.fields.not_set') }}</span>
          </button>
        </div>

        <div class="field-actions">
          <template v-if="editingKey === value.field_key">
            <LeadButton
              v-if="value.type === 'text' || value.type === 'number'"
              variant="icon"
              :disabled="Boolean(savingKey)"
              :icon-start="IconCheck"
              @click="saveValue(value.field_key, draft)" />
            <LeadButton variant="icon" :disabled="Boolean(savingKey)" :icon-start="IconX" @click="cancelEdit" />
          </template>
          <template v-else>
            <LeadButton
              variant="icon"
              :disabled="Boolean(savingKey)"
              :icon-start="IconPencil"
              v-tooltip="$t('candidate.fields.edit')"
              @click="startEdit(value)" />
            <LeadButton
              v-if="value.value !== null && value.value !== ''"
              variant="icon"
              :disabled="Boolean(savingKey)"
              :icon-start="IconX"
              v-tooltip="$t('candidate.fields.clear')"
              @click="saveValue(value.field_key, null)" />
          </template>
        </div>
      </li>
    </ul>

    <div class="field-add" v-if="isAdding">
      <CandidateFieldInput
        :definition="asDefinition(pendingNewValue!)"
        :model-value="draft"
        :disabled="Boolean(savingKey)"
        @update:model-value="(v) => (draft = v)"
        @model-value-and-submit="(v) => saveValue(pendingNewValue!.field_key, v)"
        @submit="saveValue(pendingNewValue!.field_key, draft)"
        @cancel="cancelEdit" />
    </div>

    <div class="field-add-row">
      <CandidateFieldSelector
        :exclude="values.map((v) => v.field_key)"
        @select="onSelectField"
        @create="onCreateField" />
    </div>

    <CandidateFieldFormModal v-model="createModalShow" :initial-label="createLabel" @saved="onFieldCreated" />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import LeadButton from '@/components/generic/LeadButton.vue';
import CandidateFieldInput from '@/components/candidate/CandidateFieldInput.vue';
import CandidateFieldSelector from '@/components/candidate/CandidateFieldSelector.vue';
import CandidateFieldFormModal from '@/components/candidate/CandidateFieldFormModal.vue';
import { saveCandidateFieldValues } from '@/api/candidateFields';
import translator from '@/locale/translator';
import { IconCheck, IconPencil, IconX } from '@tabler/icons-vue';
import type { CandidateFieldDefinition, CandidateFieldValue } from '@/types/candidate';

interface Props {
  candidateEntityId: string;
  values: CandidateFieldValue[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:values', values: CandidateFieldValue[]): void;
}>();

const toast = useToast();

/* --------------------------------- STATE ----------------------------------- */
const editingKey = ref<string | null>(null);
const draft = ref<string | number | null>(null);
const savingKey = ref<string | null>(null);

// a value not yet on the candidate, added via the selector and being filled in for the first time
const pendingNewValue = ref<CandidateFieldValue | null>(null);
const isAdding = ref(false);

const createModalShow = ref(false);
const createLabel = ref('');

/* --------------------------------- METHODS -------------------------------- */
// CandidateFieldInput only needs the type/options subset of a definition, which is all a
// CandidateFieldValue already carries — this avoids a second lookup against the definitions store
const asDefinition = (value: CandidateFieldValue): CandidateFieldDefinition => ({
  id: 0,
  customer_id: 0,
  field_key: value.field_key,
  label: value.label,
  type: value.type,
  options: value.options,
  position: 0,
  created_at: null,
  modified_at: null,
  deleted_at: null,
});

const formatValue = (value: CandidateFieldValue) => {
  if (value.type !== 'select' || value.value === null) return value.value;
  const choice = value.options?.choices.find((c) => c.value === value.value);
  return choice?.label ?? value.value;
};

const startEdit = (value: CandidateFieldValue) => {
  isAdding.value = false;
  pendingNewValue.value = null;
  editingKey.value = value.field_key;
  draft.value = value.value;
};

const cancelEdit = () => {
  editingKey.value = null;
  isAdding.value = false;
  pendingNewValue.value = null;
  draft.value = null;
};

const onSelectField = (field: CandidateFieldDefinition) => {
  isAdding.value = true;
  pendingNewValue.value = {
    field_key: field.field_key,
    label: field.label,
    type: field.type,
    options: field.options,
    value: null,
    updated_at: null,
    updated_by_user_id: null,
  };
  draft.value = null;
};

const onCreateField = (label: string) => {
  createLabel.value = label;
  createModalShow.value = true;
};

const onFieldCreated = (field: CandidateFieldDefinition) => {
  onSelectField(field);
};

const saveValue = async (fieldKey: string, value: string | number | null) => {
  savingKey.value = fieldKey;
  try {
    const updated = await saveCandidateFieldValues(props.candidateEntityId, [{ field_key: fieldKey, value }], {
      skipDefaultErrorHandler: true,
    });
    emit('update:values', updated);
    editingKey.value = null;
    isAdding.value = false;
    pendingNewValue.value = null;
    draft.value = null;
  } catch (error) {
    console.error('Error saving candidate field value:', error);
    toast.error(translator.get('candidate.fields.save_error'));
  } finally {
    savingKey.value = null;
  }
};
</script>

<style scoped>
.candidate-fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fields-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 4px;
  border-bottom: 1px solid #eaecf0;
}

.field-row:last-child {
  border-bottom: none;
}

.field-label {
  flex: 0 0 140px;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
}

.field-value {
  flex: 1 1 auto;
  min-width: 0;
}

.field-value-display {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  padding: 4px 0;
  font-size: 13px;
  color: #101828;
  cursor: pointer;
}

.field-value-display:hover {
  text-decoration: underline;
}

.field-actions {
  flex: 0 0 auto;
  display: flex;
  gap: 4px;
}

.field-add {
  padding: 8px 4px;
}

.field-add-row {
  padding-top: 8px;
}
</style>
